import { Injectable, inject } from '@angular/core';

import { NavigationService } from '@frontend/vanilla/core';
import { Subscription, interval } from 'rxjs';

import { ChannelType } from '../config/appConstants';
import { UrlConstants } from '../config/urlConstants';
import { ResponseStatus } from '../shared/error.constants';
import { DataService } from './data.service';
import { GtmService } from './gtm.service';
import { LoaderService } from './loader.service';
import { QdApiService } from './qd.service';
import { ResizeService } from './resize.service';

@Injectable({
    providedIn: 'root',
})
export class DepositRedirectService {
    private navigationService = inject(NavigationService);
    redirectMessage: boolean;
    depositSubmitResponse: any;
    redirectionUrl: any;
    redirectType: string = '';
    submitCreq: boolean;
    creq: any;
    redirectionData: any;
    intervalTimer: Subscription;
    cvv2: string;
    popupBlocked: boolean;
    openInCustomTab: boolean = false;
    new_3ds2_route: boolean = false;
    depositProcess: boolean = false;
    callback: any;

    constructor(
        private dataService: DataService,
        private gtmService: GtmService,
        private loaderService: LoaderService,
        private apiService: QdApiService,
        private resizer: ResizeService,
    ) {
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    redirectToThirdParty(callback: any) {
        this.loaderService.isLoading.next(true);
        const target = 'TheWindow';
        let childWindow;
        let redirectionActionUrl = '';
        const buildRedirectionUrl = (formType: string, params: Record<string, string>) => {
            let url = `${this.navigationService.location.baseUrl()}/payments/PostDepositRedirection?formtype=${formType}`;
            Object.entries(params).forEach(([key, value]) => {
                url += `&${key}=${window.encodeURIComponent(value)}`;
            });
            return url;
        };
        const sessionInfo = this.dataService?.sessionInfo;
        if ([ChannelType.AN, ChannelType.IN, ChannelType.AW, ChannelType.IW, ChannelType.IH].includes(sessionInfo?.channelId)) {
            if (this.creq) {
                redirectionActionUrl = buildRedirectionUrl('creq', { creq: this.creq, redirectionUrl: this.redirectionUrl });
            } else if (this.depositSubmitResponse?.postData) {
                const { postData, redirectionBackUrl } = this.depositSubmitResponse;

                if (postData.JWT) {
                    redirectionActionUrl = buildRedirectionUrl('JWT', { JWT: postData.JWT, MD: postData.MD, redirectionUrl: this.redirectionUrl });
                } else if (postData.PaReq) {
                    redirectionActionUrl = buildRedirectionUrl('PaReq', {
                        PaReq: postData.PaReq,
                        TermUrl: redirectionBackUrl,
                        MD: postData.MD,
                        redirectionUrl: this.redirectionUrl,
                    });
                }
            }
        }
        const paymentMethod = this.dataService?.depositInfo?.paymentMethod;
        if (this.openInCustomTab && redirectionActionUrl !== '') {
            this.handleRedirection(redirectionActionUrl, this.dataService.sessionInfo);
            this.checkDepositStatus(this.depositSubmitResponse?.transactionId, callback);
            this.gtmService.raiseEvent('quickDepositLoad', paymentMethod?.toLowerCase() + 'Redirection', paymentMethod);
        } else {
            const url = this.redirectType === 'GET' ? this.redirectionUrl : redirectionActionUrl;

            childWindow = window.open(url, target, 'status=0,title=0,height=600,width=800,scrollbars=yes');

            if (childWindow) {
                this.popupBlocked = false;
                if (this.redirectType === 'POST' && redirectionActionUrl === '') {
                    (<HTMLFormElement>document.getElementById('depositpostredirectform')).submit();
                }
                childWindow.focus();
                this.checkDepositStatus(this.depositSubmitResponse?.transactionId, callback);
                this.gtmService.raiseEvent('quickDepositLoad', paymentMethod?.toLowerCase() + 'Redirection', paymentMethod);
            } else {
                this.popupBlocked = true;
                this.loaderService.isLoading.next(false);
                this.resizer.resizeCashier();
            }
        }
    }

    checkDepositStatus(transactionID: any, callback: any, childWindow: any = null) {
        this.callback = callback;
        this.intervalTimer = interval(3000).subscribe(() => {
            const pathParam = `${UrlConstants.DEPOSIT_STATUS_URI}/${transactionID}`;
            this.apiService.get(pathParam).subscribe(
                (response) => {
                    if (response.status !== ResponseStatus.PENDING) {
                        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
                        if (response.status == ResponseStatus.SUCCESS) {
                            this.dataService._callSuccessApi = true;
                        }
                        this.closeWindowAndCleanup(childWindow);
                        callback(response);
                    }
                },
                (rejected) => {
                    this.closeWindowAndCleanup(childWindow);
                    callback(rejected);
                },
            );
        });
    }
    private handleRedirection(redirectionActionUrl: string, sessionInfo: any): void {
        if (this.new_3ds2_route) {
            const url = this.getCustomUrl(sessionInfo?.channelId, redirectionActionUrl);
            if (url) {
                this.openInCustomTabUrl(url);
            }
        } else {
            this.openInCustomTabUrl(redirectionActionUrl);
        }
    }
    private openInCustomTabUrl(url: string): void {
        this.navigationService.goTo(url, { forceReloadTarget: 'top' });
    }
    private getCustomUrl(channelId: string, redirectionActionUrl: string): string {
        let url = '';
        if ([ChannelType.AN, ChannelType.AW].includes(channelId)) {
            url = 'cashierex://?openinBrowser=' + window.encodeURIComponent(redirectionActionUrl);
        } else if ([ChannelType.IN, ChannelType.IW, ChannelType.IH].includes(channelId)) {
            url = 'bwinex://?openinBrowser=' + window.encodeURIComponent(redirectionActionUrl);
        }
        return url;
    }
    private closeWindowAndCleanup(childWindow: any): void {
        if (childWindow) childWindow.close();
        this.intervalTimer.unsubscribe();
    }

    check3dDepositStatus(callback: any) {
        let is3dResponseReceived = false;
        const url = `${UrlConstants.THREEDSMETHOD_STATUS_URI}/${this.depositSubmitResponse?.transactionId}`;
        const intervalTimer: Subscription = interval(2000).subscribe(() => {
            this.apiService.get(url).subscribe((data) => {
                intervalTimer.unsubscribe();
                is3dResponseReceived = true;
                callback(data);
            });
        });

        setTimeout(() => {
            if (!is3dResponseReceived) {
                intervalTimer.unsubscribe();
                callback();
            }
        }, 10000);
    }

    getRedirectData = (callback: any) => {
        const url = `${UrlConstants.REDIRECTION_DATA_URI}/${this.depositSubmitResponse?.transactionId}`;
        this.apiService.get(url).subscribe((response: any) => {
            callback(response);
        });
    };
    handleVisibilityChange() {
        if (document.visibilityState === 'hidden') {
            this.intervalTimer.unsubscribe();
        } else {
            this.intervalTimer.unsubscribe();
            this.checkDepositStatus(this.depositSubmitResponse?.transactionId, this.callback);
        }
    }
}
